import { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import Dashboard from "./pages/Dashboard";
import NewShoot from "./pages/NewShoot";
import BulkUpload from "./pages/BulkUpload";
import MyShoots from "./pages/MyShoots";
import ShootDetail from "./pages/ShootDetail";
import Downloads from "./pages/Downloads";
import UsageBilling from "./pages/UsageBilling";
import Profile from "./pages/Profile";
import TeamPage from "./pages/TeamPage";
import APIKeysPage from "./pages/APIKeysPage";
import Products from "./pages/Products";
import AddProduct from "./pages/AddProduct";
import ProductDetail from "./pages/ProductDetail";
import CreateShoot from "./pages/CreateShoot";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminModels from "./pages/admin/AdminModels";
import AdminBackgrounds from "./pages/admin/AdminBackgrounds";
import AIJobsMonitor from "./pages/admin/AIJobsMonitor";
import ShowcaseManager from "./pages/admin/ShowcaseManager";
// Legal Pages
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionPage from "./pages/SubscriptionPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, requiresSubscription = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const checkSubscription = async () => {
      if (user && requiresSubscription) {
        try {
          const response = await axios.get(`${API}/user/subscription`, { withCredentials: true });
          setSubscription(response.data);
        } catch (error) {
          setSubscription(null);
        }
      }
      setCheckingSubscription(false);
    };
    
    if (!loading) {
      checkSubscription();
    }
  }, [user, loading, requiresSubscription]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect to pricing if user has no active subscription and requires one
  // Allow access to pricing and subscription pages without subscription
  const allowedWithoutSub = ['/pricing', '/subscription', '/profile'];
  if (requiresSubscription && !subscription && !allowedWithoutSub.includes(location.pathname)) {
    return <Navigate to="/pricing" state={{ message: "Please subscribe to access the dashboard" }} replace />;
  }

  if (adminOnly && !["admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        login(response.data.user);
        toast.success("Welcome to JiniStudio AI!");
        navigate("/dashboard", { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/");
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-neutral-600 font-medium">Authenticating...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Legal/Public Pages */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            <AddProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:skuId"
        element={
          <ProtectedRoute>
            <ProductDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:skuId/shoot"
        element={
          <ProtectedRoute>
            <CreateShoot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-shoot"
        element={
          <ProtectedRoute>
            <NewShoot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulk-upload"
        element={
          <ProtectedRoute>
            <BulkUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shoots"
        element={
          <ProtectedRoute>
            <MyShoots />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shoots/:shootId"
        element={
          <ProtectedRoute>
            <ShootDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/downloads"
        element={
          <ProtectedRoute>
            <Downloads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage"
        element={
          <ProtectedRoute>
            <UsageBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requiresSubscription={false}>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute requiresSubscription={false}>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/developer-keys"
        element={
          <ProtectedRoute>
            <APIKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/models"
        element={
          <ProtectedRoute adminOnly>
            <AdminModels />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backgrounds"
        element={
          <ProtectedRoute adminOnly>
            <AdminBackgrounds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute adminOnly>
            <AIJobsMonitor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/showcase"
        element={
          <ProtectedRoute adminOnly>
            <ShowcaseManager />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
